import { ISearchResultsWidgetStyle } from "@pageBuilder/typings";
import { CssProperties } from "src/models/css-property";
import { WidgetAnimationRenderer } from "../widget-animation.renderer";
import { ElementStyleService } from "../../../services/element-style.service";

export class SearchResultsStyleRenderer {

    private readonly animationRenderer: WidgetAnimationRenderer;

    constructor(private readonly styleService: ElementStyleService) {
        this.animationRenderer = new WidgetAnimationRenderer(this.styleService);
    }

    public render(element: HTMLElement, style: ISearchResultsWidgetStyle, includeSearchBox: boolean): void {
        this.renderCommon(element, style);
        this.renderSearchBoxStyles(element, style, includeSearchBox);
        this.renderSearchBoxHoverStyles(element, style, includeSearchBox);
        this.renderSearchResultsStyles(element, style);
    }

    private renderCommon(element: HTMLElement, style: ISearchResultsWidgetStyle): void {
        const styles = new CssProperties();

        if (style.font.family) {
            styles.add("font-family", style.font.family);
        }

        if (style.font.weight) {
            styles.add("font-weight", style.font.weight);
        }

        this.styleService.setStyle(element, `#${element.id} .widget-content`, styles.data);
    }

    private renderSearchBoxStyles(element: HTMLElement, style: ISearchResultsWidgetStyle, includeSearchBox: boolean): void {
        if (!includeSearchBox) {
            return;
        }

        const inputStyles = new CssProperties();

        if (style.searchBoxStyle?.fillingColor?.colorDefault) {
            inputStyles.add("background-color", style.searchBoxStyle.fillingColor.colorDefault);
        }

        if (style.searchBoxStyle?.borderThickness) {
            inputStyles.add("border-width", style.searchBoxStyle.borderThickness);
        }

        if (style.searchBoxStyle?.borderColor?.colorDefault) {
            inputStyles.add("border-color", style.searchBoxStyle.borderColor.colorDefault);
        }

        if (style.searchBoxStyle?.radius) {
            const radius = style.searchBoxStyle.radius;
            inputStyles.add("border-radius", `${radius.topLeftPx}px ${radius.topRightPx}px ${radius.bottomRightPx}px ${radius.bottomLeftPx}px`);
        }

        if (style.searchBoxStyle?.textColor) {
            inputStyles.add("color", style.searchBoxStyle.textColor);
        }

        if (style?.font?.family) {
            inputStyles.add("font-family", style?.font?.family);
        }

        if (style?.font?.weight) {
            inputStyles.add("font-weight", style?.font?.weight);
        }

        this.styleService.setStyle(element, `#${element.id} input`, inputStyles.data);

        const iconStyles = new CssProperties();
        if (style.searchBoxStyle?.iconsColor) {
            iconStyles.add("--icon-color", style.searchBoxStyle.iconsColor);
            this.styleService.setStyle(element, `#${element.id} .search-input-wrapper`, iconStyles.data);
        }

        const placeholderStyles = new CssProperties();
        if (style.searchBoxStyle?.placeholderColor?.colorDefault) {
            placeholderStyles.add("color", style.searchBoxStyle.placeholderColor.colorDefault);
            this.styleService.setStyle(element, `#${element.id} input::placeholder`, placeholderStyles.data);
        }
    }

    private renderSearchBoxHoverStyles(element: HTMLElement, style: ISearchResultsWidgetStyle, includeSearchBox: boolean): void {
        if (!includeSearchBox) {
            return;
        }

        const inputStyles = new CssProperties();

        if (style.searchBoxStyle?.fillingColor?.colorHovered) {
            inputStyles.add("background-color", style.searchBoxStyle.fillingColor.colorHovered);
        }

        if (style.searchBoxStyle?.borderColor?.colorHovered) {
            inputStyles.add("border-color", style.searchBoxStyle.borderColor.colorHovered);
        }

        this.styleService.setStyle(element, `#${element.id} input:hover`, inputStyles.data);

        const placeholderStyles = new CssProperties();
        if (style.searchBoxStyle?.placeholderColor?.colorHovered) {
            placeholderStyles.add("color", style.searchBoxStyle.placeholderColor.colorHovered);
            this.styleService.setStyle(element, `#${element.id} input:hover::placeholder`, placeholderStyles.data);
        }
    }

    private renderSearchResultsStyles(element: HTMLElement, style: ISearchResultsWidgetStyle): void {
        const tabStyles = new CssProperties();
        if (style.resultsContentStyle?.tabColor?.colorDefault) {
            tabStyles.add("color", style.resultsContentStyle?.tabColor?.colorDefault);
            this.styleService.setStyle(element, `#${element.id} .nav-link`, tabStyles.data);
        }

        const activeTabStyles = new CssProperties();
        if (style.resultsContentStyle?.tabColor?.colorClicked) {
            activeTabStyles.add("color", style.resultsContentStyle?.tabColor?.colorClicked);
            this.styleService.setStyle(element, `#${element.id} .active .nav-link`, activeTabStyles.data);
            this.styleService.setStyle(element, `#${element.id} .nav-link:hover`, activeTabStyles.data);
        }

        const activeTabBorderStyles = new CssProperties();
        if (style.resultsContentStyle.tabColor.colorClicked) {
            activeTabBorderStyles.add("border-color", style.resultsContentStyle.tabColor.colorClicked);
            this.styleService.setStyle(element, `#${element.id} .nav-item.active`, activeTabBorderStyles.data);
            this.styleService.setStyle(element, `#${element.id} .nav-item:hover`, activeTabBorderStyles.data);
        }

        const resultMessageStyles = new CssProperties();
        if (style.resultsContentStyle?.resultMessageColor) {
            resultMessageStyles.add("color", style.resultsContentStyle?.resultMessageColor);
            this.styleService.setStyle(element, `#${element.id} .result-found-label`, resultMessageStyles.data);
        }

        const headingColorStyles = new CssProperties();
        if (style.resultsContentStyle?.headingColor) {
            headingColorStyles.add("color", style.resultsContentStyle?.headingColor);
            this.styleService.setStyle(element, `#${element.id} .search-results .title`, headingColorStyles.data);
        }

        const titleColorStyles = new CssProperties();
        if (style.resultsContentStyle?.titleColor?.colorDefault) {
            titleColorStyles.add("color", style.resultsContentStyle.titleColor.colorDefault);
            this.styleService.setStyle(element, `#${element.id} .result-title`, titleColorStyles.data);
        }

        const titleHoverColorStyles = new CssProperties();
        if (style.resultsContentStyle?.titleColor?.colorHovered) {
            titleHoverColorStyles.add("color", style.resultsContentStyle.titleColor.colorHovered);
            this.styleService.setStyle(element, `#${element.id} .search-result-link:hover .result-title`, titleHoverColorStyles.data);
        }

        const textColorStyles = new CssProperties();
        if (style.resultsContentStyle?.textColor?.colorDefault) {
            textColorStyles.add("color", style.resultsContentStyle.textColor.colorDefault);
            this.styleService.setStyle(element, `#${element.id} .result-description`, textColorStyles.data);
        }

        const textHoverColorStyles = new CssProperties();
        if (style.resultsContentStyle?.textColor?.colorHovered) {
            textHoverColorStyles.add("color", style.resultsContentStyle.textColor.colorHovered);
            this.styleService.setStyle(element, `#${element.id} .search-result-link:hover .result-description`, textHoverColorStyles.data);
        }

        const paginationBtnColorStyles = new CssProperties();
        if (style.resultsContentStyle?.paginationButtonColor?.colorDefault) {
            paginationBtnColorStyles.add("color", style.resultsContentStyle.paginationButtonColor.colorDefault);
            this.styleService.setStyle(element, `#${element.id} .load-more`, paginationBtnColorStyles.data);
            this.styleService.setStyle(element, `#${element.id} .view-all-link`, paginationBtnColorStyles.data);
        }

        const paginationBtnHoverColorStylesStyles = new CssProperties();
        if (style.resultsContentStyle?.paginationButtonColor?.colorHovered) {
            paginationBtnHoverColorStylesStyles.add("color", style.resultsContentStyle.paginationButtonColor.colorHovered);
            this.styleService.setStyle(element, `#${element.id} .load-more:hover`, paginationBtnHoverColorStylesStyles.data);
            this.styleService.setStyle(element, `#${element.id} .view-all-link:hover`, paginationBtnHoverColorStylesStyles.data);
        }
    }

}
